import { useEffect } from "react";

/**
 * Ends an in-flight move drag from signals that outlive the dragged row.
 *
 * The drag source cannot be the only way out of a drag: the document list
 * virtualises its rows, so native auto-scroll unmounts the very row the user is
 * dragging, and a detached node's `dragend` never reaches React's root
 * listener. The drag state then sticks forever — every folder, tree node and
 * breadcrumb keeps its "drop here" ring, and the next drag is validated against
 * a stale selection.
 *
 * `drop` and `dragend` on the document cover a drag that finishes normally.
 * The first pointer move afterwards covers the rest — a drag cancelled with
 * Escape, or dropped outside the window — because the browser suppresses
 * pointer events for the whole native drag and only resumes once it is over.
 */
export function useMoveDragTeardown(isDragActive: boolean, onDragEnd: () => void) {
  useEffect(() => {
    if (!isDragActive) return;
    const events = ["dragend", "drop", "pointermove"] as const;
    events.forEach((type) => document.addEventListener(type, onDragEnd, true));
    return () => events.forEach((type) => document.removeEventListener(type, onDragEnd, true));
  }, [isDragActive, onDragEnd]);
}
